/**
 * Protected Route Component - Production-Grade Authentication Guard
 * Ensures only authenticated users can access protected routes
 * Redirects unauthorized users to login with return URL
 * Date: 13-09-2025 14:58:11
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { RouteLoader } from '../../../components/ui/loader';
import { User } from '../../../components/layout/Header/Header.types';
import { addLocaleToPath } from '../../../lib/i18n';
import { useAuthOverlay } from '../../auth/AuthOverlayContext';

const AuthRouteGate: React.FC<{
  localeRedirectTo: string;
  onOpen: () => void;
}> = ({ localeRedirectTo, onOpen }) => {
  React.useEffect(() => {
    onOpen();
  }, [onOpen]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl border border-gray-100 bg-white/80 p-4 shadow-xs backdrop-blur-sm">
        <p className="text-sm font-semibold text-gray-900">Sign in required</p>
        <p className="mt-0.5 text-xs text-gray-500">To access this page</p>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-gray-900/20"
          >
            Sign in
          </button>
          <a
            href={localeRedirectTo}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-900/10"
          >
            Full page
          </a>
        </div>
      </div>
    </div>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Minimum required role for access (optional)
   * Defaults to any authenticated user
   */
  requiredRole?: 'user' | 'admin';
  /**
   * Custom redirect path (optional)
   * Defaults to /login
   */
  redirectTo?: string;
}

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication
 *
 * Features:
 * - Authentication check
 * - Role-based access control
 * - Return URL preservation
 * - Loading state handling
 * - Proper error boundaries
 *
 * @example
 * ```tsx
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 *
 * <ProtectedRoute requiredRole="admin" redirectTo="/admin/login">
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = 'user',
  redirectTo = '/login'
}) => {
  const { authState } = useAuth();
  const location = useLocation();
  const { i18n, t } = useTranslation('auth');
  const { openAuthSheet } = useAuthOverlay();

  // Build a locale-aware redirect path so /login becomes /en/login or /ar/login
  const localeRedirectTo = addLocaleToPath(redirectTo, i18n.language);

  // Show loading spinner while auth state is being determined
  if (authState.isLoading) {
    return <RouteLoader isLoading={true} />;
  }

  // Check if user is authenticated
  if (!authState.isAuthenticated || !authState.user) {
    const returnTo = location.pathname + location.search;

    return (
      <AuthRouteGate
        localeRedirectTo={localeRedirectTo}
        onOpen={() =>
          openAuthSheet({
            returnTo,
            reason: t('authSheet.signInToContinue', 'Sign in to continue')
          })
        }
      />
    );
  }

  // Check role-based access if required
  if (requiredRole && authState.user.role !== requiredRole) {
    // Redirect to appropriate page based on user's role
    const fallbackPath =
      authState.user.role === 'admin' ? '/admin' : '/dashboard';

    return (
      <Navigate
        to={fallbackPath}
        state={{
          message: 'You do not have permission to access this page'
        }}
        replace
      />
    );
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

/**
 * Higher-order component for protecting routes
 * Alternative API for route protection
 *
 * @example
 * ```tsx
 * const ProtectedDashboard = withProtection(DashboardPage);
 * ```
 */
export const withProtection = <P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
): React.FC<P> => {
  return (props: P): React.JSX.Element => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

/**
 * Hook for checking authentication status in components
 * Useful for conditional rendering based on auth state
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { isAuthenticated, user, hasRole } = useProtectedRoute();
 *
 *   if (!isAuthenticated) {
 *     return <LoginPrompt />;
 *   }
 *
 *   return <Dashboard user={user} />;
 * };
 * ```
 */
export const useProtectedRoute = (): {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
} => {
  const { authState } = useAuth();

  return {
    isAuthenticated: authState.isAuthenticated && !!authState.user,
    user: authState.user,
    isLoading: authState.isLoading,
    hasRole: (role: string) => authState.user?.role === role
  };
};

export default ProtectedRoute;
